import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../../utils/axios';

const API_URL = '/api/dcp-nodes/';

export const fetchDCPNodes = createAsyncThunk(
  'dcpNodes/fetchNodes',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const fetchDCPNodeStats = createAsyncThunk(
  'dcpNodes/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL}stats/summary`);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const createDCPNode = createAsyncThunk(
  'dcpNodes/createNode',
  async (nodeData, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL, nodeData, {
        headers: { 'Content-Type': 'application/json' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const updateDCPNode = createAsyncThunk(
  'dcpNodes/updateNode',
  async ({ nodeId, nodeData }, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(`${API_URL}${nodeId}`, nodeData, {
        headers: { 'Content-Type': 'application/json' },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const checkDCPNodeHealth = createAsyncThunk(
  'dcpNodes/checkHealth',
  async (nodeId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}${nodeId}/check-health`, {});
      return { nodeId, ...data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const deleteDCPNode = createAsyncThunk(
  'dcpNodes/deleteNode',
  async (nodeId, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}${nodeId}`);
      return nodeId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const restartDCPNodeServices = createAsyncThunk(
  'dcpNodes/restartServices',
  async (nodeId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}${nodeId}/restart-services`, {});
      return { nodeId, ...data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const bootstrapDCPNode = createAsyncThunk(
  'dcpNodes/bootstrap',
  async (nodeId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}${nodeId}/bootstrap`, {}, { timeout: 60000 });
      return { nodeId, ...data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const provisionDCPNode = createAsyncThunk(
  'dcpNodes/provision',
  async ({ nodeId, config }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}${nodeId}/provision`, config, { timeout: 300000 });
      return { nodeId, ...data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

export const rebootDCPNode = createAsyncThunk(
  'dcpNodes/reboot',
  async (nodeId, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${API_URL}${nodeId}/reboot`, {});
      return { nodeId, ...data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.detail || error.message);
    }
  }
);

const dcpNodesSlice = createSlice({
  name: 'dcpNodes',
  initialState: {
    nodes: [],
    stats: null,
    loading: false,
    error: null,
  },
  reducers: {
    clearDCPNodesError: (state) => {
      state.error = null;
    },
    updateDCPNodeStatus: (state, action) => {
      const node = state.nodes.find(n => n.id === action.payload.id);
      if (node) {
        Object.assign(node, action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDCPNodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDCPNodes.fulfilled, (state, action) => {
        state.loading = false;
        state.nodes = action.payload;
      })
      .addCase(fetchDCPNodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchDCPNodeStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(createDCPNode.fulfilled, (state, action) => {
        state.nodes.push(action.payload);
      })
      .addCase(updateDCPNode.fulfilled, (state, action) => {
        const index = state.nodes.findIndex(n => n.id === action.payload.id);
        if (index !== -1) {
          state.nodes[index] = action.payload;
        }
      })
      .addCase(checkDCPNodeHealth.fulfilled, (state, action) => {
        const node = state.nodes.find(n => n.id === action.payload.nodeId);
        if (node) {
          node.is_online = action.payload.is_online;
          node.cpu_count = action.payload.cpu_count;
          node.disk_free_gb = action.payload.disk_free_gb;
          node.dcpomatic_installed = action.payload.dcpomatic_installed;
        }
      })
      .addCase(deleteDCPNode.fulfilled, (state, action) => {
        const node = state.nodes.find(n => n.id === action.payload);
        if (node) {
          node.is_active = false;
          node.is_online = false;
        }
      });
  },
});

export const { clearDCPNodesError, updateDCPNodeStatus } = dcpNodesSlice.actions;
export default dcpNodesSlice.reducer;
